<template>
    <modal
        name="pay-with-piggy-gift-card"
        height="auto"
        width="575"
        :maxWidth="575"
        :adaptive="true"
        :scrollable="true"
        :clickToClose="false"
        :pivotY="pivotY"
        :reset="true"
        transition="pop-out"
        :class="{ 'mobile-item-modal': pivotY === 1 }"
    >
        <div class="item-modal-content relative flex flex-col w-full">
            <div class="item-modal-body">
                <div
                    class="item-modal-details flex-auto p-6 pb-1 overflow-y-auto"
                >
                    <div class="relative block w-full pb-3 shadow-sm">
                        <h4
                            class="font-heading text-xl font-bold text-subheading"
                        >
                            {{ $t('giftCardModal.title') }}
                        </h4>
                        <div class="absolute right-0 top-0">
                            <button
                                type="button"
                                class="text-primary transition duration-150 ease-in-out hover:text-gray-500 focus:text-gray-500 focus:outline-none"
                                @click="hide"
                            >
                                <close-icon />
                            </button>
                        </div>
                    </div>

                    <div v-if="!giftCardBalance">
                        <vue-content-loading
                            :height="48"
                            :speed="2"
                            class="mb-3"
                            primaryColor="#f3f3f3"
                            secondaryColor="#ecebeb"
                        >
                            <rect
                                x="2.5%"
                                y="10"
                                rx="3"
                                ry="3"
                                width="95%"
                                height="40"
                            />
                        </vue-content-loading>
                    </div>

                    <div class="flex flex-col py-4 px-2" v-else>
                        <div class="text-base font-medium">
                            {{ $t('giftCardModal.availableBalance') }}
                            <span class="text-primary">{{
                                (giftCardBalance / 100).toFixed(2) | currency
                            }}</span>
                        </div>

                        <div class="flex mt-4 items-center">
                            <input
                                type="checkbox"
                                id="payFull"
                                class="optin-checkbox form-checkbox h-4 w-4 cursor-pointer rounded border-gray-200 transition duration-150 ease-in-out focus:border-blue-200 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                v-model="payFull"
                                @change="setFullAmount"
                            />
                            <label
                                for="payFull"
                                class="font-medium text-sm text-gray-600 ml-2"
                            >
                                {{
                                    $t('giftCardModal.payFullAmount', {
                                        amount:
                                            (maxAmount / 100).toFixed(2) |
                                            currency
                                    })
                                }}
                            </label>
                        </div>

                        <div class="flex mt-4 items-center">
                            <label
                                for="paymentAmount"
                                class="font-medium text-sm text-gray-600 mr-4"
                            >
                                {{ $t('giftCardModal.enterAmount') }}
                            </label>
                            <currency-input
                                v-model="amount"
                                id="paymentAmount"
                                class="form-input block w-1/3 bg-transparent border focus:border-blue-300 border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200 shadow-sm resize-none transition duration-150 ease-in-out"
                                placeholder="1.00"
                                autocomplete="off"
                                :currency="null"
                                locale="en-GB"
                                :value-as-integer="true"
                                :allow-negative="false"
                                @blur="validateAmount"
                            ></currency-input>
                        </div>

                        <div v-if="error" class="text-red-600 text-sm mt-2">
                            {{ error }}
                        </div>

                        <div class="text-sm text-gray-600 mt-2">
                            {{ $t('giftCardModal.remainingToPay') }}
                            <span class="font-medium">{{
                                ((payableAmount - amount) / 100).toFixed(2)
                                    | currency
                            }}</span>
                        </div>
                    </div>
                </div>

                <div
                    class="item-modal-footer flex flex-col-reverse justify-center p-6 md:flex-row"
                >
                    <base-button
                        class="p-3 px-10 mb-3 sm:block sm:mb-0 md:w-auto"
                        size="sm"
                        variant="secondaryActions"
                        :button-text="'Close'"
                        :is-border-transparent="true"
                        @clicked="hide"
                    />
                    <base-button
                        class="px-12 py-4 sm:mb-3 mb-0 md:ml-6 md:w-auto"
                        size="sm"
                        button-text="Apply"
                        :disabled="!!error || amount <= 0"
                        @clicked="apply"
                    />
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
import { CurrencyInput } from 'vue-currency-input';
import VueContentLoading from 'vue-content-loading';
import { mapState, mapActions, mapGetters } from 'vuex';

import isMobile from '@/helpers/isMobileDevice';
import CloseIcon from '@/components/icons/CloseIcon';
import * as giftcardService from '@/services/giftcard.service';

export default {
    name: 'PayWithPiggyGiftCardModal',
    components: { CloseIcon, VueContentLoading, CurrencyInput },
    data() {
        return {
            amount: 0,
            payFull: false,
            error: null
        };
    },
    computed: {
        ...mapGetters({
            amountSelectedToPay: 'bill/amountSelected'
        }),
        ...mapState({
            totalWithDiscount: state => state.cart.totalWithDiscount
        }),
        ...mapGetters({
            billId: 'bill/id',
            venueValue: 'venue/venueValue'
        }),
        ...mapGetters({
            giftCardBalance: 'giftCard/giftCardBalance',
            giftCardProvider: 'giftCard/giftCardProvider',
            giftCardReference: 'giftCard/giftCardReference'
        }),
        pivotY() {
            return isMobile() ? 1 : 0.5;
        },
        maxAmount() {
            return Math.min(this.payableAmount, this.giftCardBalance);
        },
        payableAmount() {
            return this.billId
                ? this.amountSelectedToPay
                : this.totalWithDiscount;
        },
        venueId() {
            return this.venueValue('id');
        }
    },
    methods: {
        ...mapActions({
            redeemGiftCard: 'giftCard/redeemGiftCard'
        }),
        setFullAmount() {
            if (this.payFull) {
                this.amount = this.maxAmount;
                this.error = null;
            }
        },
        validateAmount() {
            if (this.amount < 0) {
                this.amount = 0;
                this.error = 'Amount cannot be negative';
            } else if (this.amount > this.maxAmount) {
                this.amount = this.maxAmount;
                this.error = `Amount cannot exceed ${(
                    this.maxAmount / 100
                ).toFixed(2)}`;
            } else if (this.amount === 0) {
                this.error = 'Please enter a valid amount';
            } else {
                this.error = null;
            }
        },
        async apply() {
            this.validateAmount();

            try {
                if (!this.error && this.amount > 0) {
                    if (this.billId) {
                        const payload = {
                            billId: this.billId,
                            amount: this.amount,
                            venueId: this.venueId,
                            giftCardCode: this.giftCardReference
                        };

                        await this.redeemGiftCard(payload);
                    } else {
                        this.$store.commit(
                            'giftCard/setGiftCardPayAmount',
                            this.amount
                        );

                        const newCartTotal =
                            giftcardService.calculateRemainingCartTotal(
                                this.payableAmount,
                                this.amount
                            );

                        this.$store.commit(
                            'cart/setTotalWithDiscount',
                            newCartTotal
                        );
                    }
                    this.hide();
                }
            } catch (error) {
                this.error =
                    error.response?.data?.message ||
                    this.$t('giftCardModal.failedToApply');
            }
        },
        hide() {
            this.$modal.hide('pay-with-piggy-gift-card');
        }
    }
};
</script>

<style scoped>
.optin-checkbox {
    color: #172d48;
    color: var(--color-bg-button, #292a31);
}
</style>
